import { FormControl, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/pt';
import minMax from 'dayjs/plugin/minMax';
import { type ChangeEvent, useEffect, useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { _ } from '/@/locale';
import MuiDatePicker from '/@/mui/datepicker';
import { MuiSelect } from '/@/mui/select';
import { getCropOptions } from '/@/store/crop-protection/selectors';
import type { CropProtectionForm } from '/@/types/crop-protection';
import type { Field } from '/@/types/field';
import { objectEntries } from '/@/utils';
import { assignYearsToDateRange } from '/@/utils/date';

dayjs.extend(minMax);

const CropStep = ({ field, form }: { field: Field | undefined; form: UseFormReturn<CropProtectionForm> }) => {
    useEffect(() => {
        form.reset({
            ...form.getValues(),
            type: '',
            application: '',
            weedType: '',
            weed: '',
            weedSize: '',
            recommendationOption: '',
            diseaseId: '',
            applicationType: '',
            plagueId: '',
        });
    }, [form]);
    const cropOptions = useMemo(() => getCropOptions(), []);
    const hasPlantingDateChoices = [
        { label: _('No lo he decidido aún'), value: false },
        { label: _('Tengo una fecha de siembra estimada'), value: true },
    ];
    const [hasPlantingDate, setHasPlantingDate] = useState<boolean>(false);

    useEffect(() => {
        if (form.getValues().plantingDate) {
            setHasPlantingDate(true);
        }
    }, [form]);

    useEffect(() => {
        const zone = field?.node.zones.find((z) => {
            return z.type === form.getValues().crop;
        });
        if (!zone || !zone.defaultPlantingDate || !zone.minDate || !zone.maxDate) return;
        const { defaultPlantingDate } =
            assignYearsToDateRange(zone.minDate, zone.maxDate, zone.defaultPlantingDate) || {};

        if (hasPlantingDate && !form.getValues().plantingDate && defaultPlantingDate) {
            form.setValue('plantingDate', dayjs(defaultPlantingDate));
        }
    }, [field?.node.zones, form, hasPlantingDate]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setHasPlantingDate(event.target.value === 'true');
        if (event.target.value !== 'true') {
            form.setValue('plantingDate', '');
        }
    };

    return (
        <Stack spacing={3}>
            <Stack spacing={1}>
                <Stack>
                    <Typography color='primary' variant='subtitle1' sx={{ fontWeight: 600 }}>
                        {_('Seleccioná el cultivo a proteger')}
                    </Typography>
                </Stack>
                <Stack>
                    <Typography color='primary.mui.inactive' variant='subtitle1'>
                        {_('Elegí el tipo de cultivo para recibir recomendaciones personalizadas.')}
                    </Typography>
                </Stack>
            </Stack>
            <Stack>
                <MuiSelect
                    inputLabelProps={{ sx: { color: 'secondary' } }}
                    formControlProps={{ sx: { width: { xs: '100%', md: 300 } } }}
                    inputLabel={<Typography color='primary'>{_('Cultivo')}</Typography>}
                    value={form.getValues().crop}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        form.reset({
                            ...form.getValues(),
                            crop: e.target.value,
                            application: '',
                            weedType: '',
                            weed: '',
                            type: '',
                            weedSize: '',
                        });
                    }}
                    options={objectEntries(cropOptions).map(([key, value]) => ({
                        label: <Typography color='primary'>{value.label}</Typography>,
                        value: key,
                    }))}
                    formHelperText={undefined}
                    formHelperTextProps={undefined}
                />
            </Stack>
            <Stack spacing={1}>
                <Stack>
                    <Typography color='primary' variant='subtitle1' sx={{ fontWeight: 600 }}>
                        {_('¿Cuándo planeas sembrar?')}
                    </Typography>
                </Stack>
                <Stack>
                    <FormControl>
                        <RadioGroup value={hasPlantingDate.toString()} onChange={handleChange}>
                            {hasPlantingDateChoices.map((pdc) => (
                                <FormControlLabel
                                    key={pdc.label}
                                    value={pdc.value.toString()}
                                    control={
                                        <Radio
                                            sx={{
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: 20,
                                                },
                                            }}
                                            color='secondary'
                                        />
                                    }
                                    label={
                                        <Typography variant='body2' color='primary'>
                                            {pdc.label}
                                        </Typography>
                                    }
                                />
                            ))}
                        </RadioGroup>
                    </FormControl>
                </Stack>
            </Stack>
            <Stack>
                <MuiDatePicker
                    value={form.getValues().plantingDate ? dayjs(form.getValues().plantingDate) : undefined}
                    onChange={(e) => {
                        form.setValue('plantingDate', e);
                    }}
                    label={
                        <Typography color={hasPlantingDate ? 'primary' : 'primary.mui.inactive'}>
                            {_('Fecha de siembra')}
                        </Typography>
                    }
                    disabled={!hasPlantingDate}
                    sx={{
                        width: { xs: '100%', md: 300 },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: (t) => t.palette.primary.main,
                        },
                        '& .MuiInputBase-input': {
                            color: (t) => t.palette.primary.main,
                        },
                        '& .MuiSvgIcon-root': {
                            color: (t) => t.palette.primary.main,
                        },
                    }}
                />
            </Stack>
        </Stack>
    );
};

export default CropStep;
